import { createContext, useContext } from 'react';

// What the signed-in staff member's Roles let them do (issue #252). The
// access data comes from /api/auth/me (backend
// services/permissionService.js loadAccess), read on every page load so a
// Role change applies without signing out:
//   { isAdmin, roles: [{ modules: { key: perm }, screens: { key: perm } }] }
// where perm is { create, read, update, delete }.
//
// Same rule as the backend: within one Role a screen uses its own override
// if it has one, otherwise its module's; across Roles a permission any of
// them grants is granted. The backend refuses anything this lets through
// by mistake — hiding here is for a tidy screen, not for security.

const ACTIONS = ['create', 'read', 'update', 'delete'];
export const FULL = Object.freeze({ create: true, read: true, update: true, delete: true });
export const NONE = Object.freeze({ create: false, read: false, update: false, delete: false });

export function resolveScreenAccess(access, pageKey, moduleKey) {
  if (!access || access.isAdmin) return FULL;
  const result = { ...NONE };
  for (const role of access.roles || []) {
    const perm = role.screens?.[pageKey] || (moduleKey && role.modules?.[moduleKey]) || NONE;
    for (const action of ACTIONS) result[action] = result[action] || !!perm[action];
  }
  return result;
}

// The best of several screens, for a record shown on more than one.
export function combineAccess(perms) {
  const result = { ...NONE };
  for (const perm of perms) {
    for (const action of ACTIONS) result[action] = result[action] || perm[action];
  }
  return result;
}

// App-wide: isAdmin plus a per-screen lookup. Provided in App.js.
export const PermissionsContext = createContext({ isAdmin: true, accessFor: () => FULL });

export function usePermissions() {
  return useContext(PermissionsContext);
}

// The current screen's permissions, provided around the routed screen in
// App.js. Defaults to full access outside it.
export const ScreenAccessContext = createContext(FULL);

export function useScreenAccess() {
  const perm = useContext(ScreenAccessContext);
  return {
    canCreate: perm.create,
    canRead: perm.read,
    canUpdate: perm.update,
    canDelete: perm.delete,
    readOnly: !perm.create && !perm.update && !perm.delete,
  };
}

// RowActionsMenu / ActionsMenu items that need a permission, by key, so
// every list screen's Edit / Archive / Unarchive / Delete hides without
// each screen repeating the check. `add-…` and `edit-…` items change the
// record on screen (Add Phone, Edit Classification), so they need Update.
// An item
// can also say `needs: 'update'` (or 'create' / 'delete') itself.
const KEY_NEEDS = {
  edit: 'update',
  rename: 'update',
  deactivate: 'delete',
  reactivate: 'delete',
  archive: 'delete',
  unarchive: 'delete',
  delete: 'delete',
  duplicate: 'create',
};

export function useAllowedItems(items) {
  const perm = useContext(ScreenAccessContext);
  return items.filter((item) => {
    const needs = item.needs || KEY_NEEDS[item.key] || (/^(add|edit)-/.test(String(item.key)) ? 'update' : null);
    return !needs || perm[needs];
  });
}
